import React, { useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { Link, useParams } from 'react-router-dom';
import { GET_ASSET_INFO, GET_ASSET_LOADS } from '../../../graphql/client/queries';
import { PermissionEnum } from '../../../types/PermissionEnum';
import { useAuthContext } from '../../Auth/AuthProvider';
import { Error, Loading } from '../../_common';
import EquipmentOriginDetails from './EquipmentDetails/EquipmentOriginDetails';
import EquipmentDestinationDetails from './EquipmentDetails/EquipmentDestinationDetails';
import EquipmentDriverDetails from './EquipmentDetails/EquipmentDriverDetails';
import EquipmentLoadDetails from './EquipmentDetails/EquipmentLoadDetails';
import { Routes } from '../../../utils';
import EquipmentDetails from './EquipmentDetails/EquipmentDetails';
import './EquipmentProfile.scss';
import EquipmentProfileNav from './EquipmentProfileNavBar/EquipmentProfileNavBar';
import {
    GetAssestLoadsByLoadId_getAssestLoadsByLoadId as AssetLoadType,
    GetAssestLoadsByLoadId,
    GetAssetInfoByLoadId_getAssetInfoByLoadId as AssetInfoType,
    GetAssetInfoByLoadId
} from '../../../types/schemaTypes';
import { ToastContainer } from 'react-toastify';

const EquipmentProfile = ({ match }: any): React.ReactElement => {
    const [tabTitle, setTabTitle] = useState(document.title);
    const authContext = useAuthContext();

    const canViewHistory = authContext.userHasPermission(PermissionEnum.ViewAssetLoadHistory);
    const canViewAssetLoads = authContext.userHasPermission(PermissionEnum.ViewAssetLoads);
    const canEditAssetLoads = authContext.userHasPermission(PermissionEnum.EditAssetLoadDetails);
    const canAssignAssetLoads = authContext.userHasPermission(PermissionEnum.AssignAssetLoads);

    const loadId = parseInt(useParams().loadId ?? '0');
    document.title = loadId ? 'Asset Load :' + loadId : tabTitle;

    let assetLoads: AssetLoadType | undefined;
    const assetInfos: AssetInfoType[] = [];
    const { data: assetLoadsData, loading: assetLoadsLoading, error: assetLoadsError } = useQuery<GetAssestLoadsByLoadId>(GET_ASSET_LOADS, {
        variables: {
            loadId: loadId,
        },
        fetchPolicy: 'network-only',
        skip: !canViewAssetLoads,
    });

    const { data: assetInfoData, loading: assetInfoLoading, error: assetInfoError } = useQuery<GetAssetInfoByLoadId>(GET_ASSET_INFO, {
        variables: {
            loadId: loadId,
        },
        fetchPolicy: 'network-only',
        skip: !canViewAssetLoads,
    });

    if (assetLoadsData && assetLoadsData.getAssestLoadsByLoadId) {
        assetLoads = assetLoadsData.getAssestLoadsByLoadId;
    }
    if (assetInfoData && assetInfoData.getAssetInfoByLoadId) {
        assetInfoData.getAssetInfoByLoadId.map((assetInfo: any) => {
            if (assetInfo)
                assetInfos.push(assetInfo);
        })
    }

    const equipmentOriginComponent = <EquipmentOriginDetails
        originType={assetLoads?.originType}
        originJob={assetLoads?.originJob}
        originLofa={assetLoads?.originLofa}
        originYard={assetLoads?.originYard}
        originLongitude={assetLoads?.originLongitude}
        originLatitude={assetLoads?.originLatitude}
        originRegion={assetLoads?.originRegion}
        originDistrict={assetLoads?.originDistrict}
    />
    const equipmentDestinationComponent = <EquipmentDestinationDetails
        destinationType={assetLoads?.destinationType}
        destinationJob={assetLoads?.destinationJob}
        destinationLofa={assetLoads?.destinationLofa}
        destinationYard={assetLoads?.destinationYard}
        destinationLongitude={assetLoads?.destinationLongitude}
        destinationLatitude={assetLoads?.destinationLatitude}
        destinationRegion={assetLoads?.destinationRegion}
        destinationDistrict={assetLoads?.destinationDistrict}
    />
    const equipmentDriverComponent = <EquipmentDriverDetails
        loadId={loadId}
        status={assetLoads?.status}
        trailerType={assetLoads?.trailerType}
        trailer={assetLoads?.trailer}
        truckNumber={assetLoads?.truckNumber}
        carrierName={assetLoads?.carrierName}
        driverName={assetLoads?.driverName}
        showRFD={assetInfos.length > 0}
    />
    const equipmentLoadComponent = <EquipmentLoadDetails
        assetLoadType={assetLoads?.assetLoadType}
        status={assetLoads?.status}
        miles={assetLoads?.miles}
        accessorialCost={assetLoads?.accessorialCost}
        payCode={assetLoads?.payCode}
    />

    const equipmentDetailsComponent = <EquipmentDetails
        assetInfo={assetInfos}
        assetInfoLoading={assetInfoLoading}
        assetInfoError={assetInfoError}
    />

    if (assetLoadsLoading && assetInfoLoading) {
        return (
            <div className="EditEquipment-SpinnerContainer">
                <Loading />
            </div>
        )
    }

    if (!canViewAssetLoads) {
        return (
            <div className="NoResult-Container Inventory-Unauth-Container">
                <div className="NoResult-First-Title-Secondary">Unauthorized</div>
                <div className="NoResult-Second-Title-Secondary">You do not have permission to view this screen</div>
            </div>
        )
    }

    const assetLoadsErr = assetLoadsError as { graphQLErrors?: Array<{ extensions?: { response?: { body?: { Message?: string } } } }> } | null | undefined;
    if (assetLoadsErr?.graphQLErrors?.[0]?.extensions?.response?.['body']?.Message?.includes('deleted')) {
        return (
            <div className="EquipmentProfile-Container">
                <div className="EquipmentProfile-Header">
                    <div className="EquipmentProfile-HeaderTitle">
                        Load #{loadId}
                        {
                            assetLoads?.status === "Delivered" && (
                                <div className="EquipmentProfile-Delivered-Label">
                                    <i className="material-icons EquipmentProfile-Delivered-Icon">
                                        check_circle_outline
                                    </i>
                                    <span className="EquipmentProfile-Delivered-Text">DELIVERED</span>
                                </div>
                            )
                        }
                        {
                            assetLoads?.status === "Canceled" && (
                                <div className="EquipmentProfile-Delivered-Label">
                                    <i className="material-icons EquipmentProfile-Delivered-Icon">
                                        cancel
                                    </i>
                                    <span className="EquipmentProfile-Delivered-Text">CANCELED</span>
                                </div>
                            )
                        }
                    </div>
                    <div className="Row EquipmentProfile-ToggleButtonsContainer">
                        <div className="EquipmentProfile-LeftToggleButton EquipmentProfile-ActiveButton">
                            <p className="EquipmentProfile-ButtonText">Load Details</p>
                        </div>
                        <div
                            className={`EquipmentProfile-RightToggleButton ${!canViewHistory &&
                                'EquipmentProfile-RightToggleButton-Disabled'}`}
                        >
                            <Link
                                to={`${Routes.EQUIPMENT_HISTORY.route}${loadId}`}
                                className="EquipmentProfile-ButtonText"
                            >
                                Load History
                            </Link>
                        </div>
                    </div>
                </div>

                <div className="NoResult-Container EquipmentProfile-Unauth-Container">
                    <div className="NoResult-First-Title-Secondary">Load Deleted</div>
                    <div className="NoResult-Second-Title-Secondary">This load is already deleted!</div>
                </div>
            </div>
        )
    }

    return (
        <div className="EquipmentProfile-Container">
            <ToastContainer />

            <div className="EquipmentProfile-Header">
                <div className="EquipmentProfile-HeaderTitle">
                    Load #{loadId}
                    {
                        assetLoads?.status === "Delivered" && (
                            <div className="EquipmentProfile-Delivered-Label">
                                <i className="material-icons EquipmentProfile-Delivered-Icon">
                                    check_circle_outline
                                </i>
                                <span className="EquipmentProfile-Delivered-Text">DELIVERED</span>
                            </div>
                        )
                    }
                    {
                        assetLoads?.status === "Canceled" && (
                            <div className="EquipmentProfile-Delivered-Label">
                                <i className="material-icons EquipmentProfile-Delivered-Icon">
                                    cancel
                                </i>
                                <span className="EquipmentProfile-Delivered-Text">CANCELED</span>
                            </div>
                        )
                    }
                </div>
                <div className="Row EquipmentProfile-ToggleButtonsContainer">
                    <div className="EquipmentProfile-LeftToggleButton EquipmentProfile-ActiveButton">
                        <p className="EquipmentProfile-ButtonText">Load Details</p>
                    </div>
                    <div
                        className={`EquipmentProfile-RightToggleButton ${!canViewHistory &&
                            'EquipmentProfile-RightToggleButton-Disabled'}`}
                    >
                        <Link
                            to={`${Routes.EQUIPMENT_HISTORY.route}${loadId}`}
                            className="EquipmentProfile-ButtonText"
                        >
                            Load History
                        </Link>
                    </div>
                </div>
            </div>

            <div className="EquipmentProfile-EquipmentDetailsContainer">
                {assetLoadsLoading ? (
                    <div className="EquipmentProfile-Loading">
                        <Loading />
                    </div>
                ) : assetLoadsError ? (
                    <div className="EquipmentProfile-Graph-Error">
                        <Error error={assetLoadsError} />
                    </div>
                ) : (
                    <div className="EquipmentProfile-EquipmentDetails">
                        {equipmentOriginComponent}
                        {equipmentDestinationComponent}
                        {equipmentDriverComponent}
                        {equipmentLoadComponent}
                        {(canEditAssetLoads || canAssignAssetLoads) && assetLoads?.status !== "Canceled" && (
                            <div className='EquipmentProfile-EditLink'>
                                <Link
                                    to={{
                                        pathname: `${Routes.EDIT_ASSET_LOAD.route}${loadId}`,
                                    }}
                                    className="EquipmentProfile-Edit"
                                >
                                    Edit
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div className="EquipmentProfile-EquipmentDetailsContainer">
                {equipmentDetailsComponent}
            </div>

            <div className="EquipmentProfile-Nav">
                <EquipmentProfileNav loadId={loadId} assetLoad={assetLoads} />
            </div>
        </div>
    );
}

export default EquipmentProfile;
